import { CalculatedFees } from '@/apis/payments/requests'
import { EllipsesLoader } from '@/app/common/components/EllipsesLoader'
import { FailCircleIcon } from '@/app/common/icons/general'
import { getTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { currencyFormatter } from '@/app/common/utilities'
import { getTotalServiceFeeToBePaidByApplicant } from '@/app/common/utils/payment/utils'
import { Label } from '@/shad/ui/label'
import { UseQueryResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

interface Props {
  paymentFeesQuery: UseQueryResult<CalculatedFees, Error>
  isEnabled: boolean
}

function PaymentFeesResultsCard(props: Props) {
  const { paymentFeesQuery, isEnabled } = props
  const { t: translate } = useTranslation()

  // placeholder UI if nothing has been typed into the input.
  if (!isEnabled) {
    return (
      <div>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="service_fee"
        >
          <Label id="service_fee_label" htmlFor="service_fee">
            {translate('service_fee')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(0)}
          </div>
        </section>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="amount_from_wallet"
        >
          <Label id="amount_from_wallet_label" htmlFor="amount_from_wallet">
            {translate('amount_from_wallet')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(0)}
          </div>
        </section>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="amount_to_provider"
        >
          <Label id="amount_to_provider_label" htmlFor="amount_to_provider">
            {translate('amount_to_provider')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(0)}
          </div>
        </section>
      </div>
    )
  }

  if (paymentFeesQuery.status === 'pending') {
    return (
      <div className="flex items-center justify-center py-10 flex-col">
        <EllipsesLoader size="lg" />
        <div className="text-sm font-medium text-gray-800 mt-2">
          Loading Payment Information
        </div>
      </div>
    )
  }

  if (paymentFeesQuery.status === 'error') {
    return (
      <div>
        <FailCircleIcon />
        <div className="text-sm font-medium">
          Error fetching payment information
        </div>
      </div>
    )
  }

  const totalAmount = getTotalAmountToBePaidByApplicant(paymentFeesQuery.data)
  const { base_amount, service_fee, serviceFeePayer } = paymentFeesQuery.data

  return (
    <div>
      <div>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="service_fee"
        >
          <Label id="service_fee_label" htmlFor="service_fee">
            {translate('service_fee')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(
              getTotalServiceFeeToBePaidByApplicant(
                service_fee,
                serviceFeePayer
              )
            )}
          </div>
        </section>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="amount_from_wallet"
        >
          <Label id="amount_from_wallet_label" htmlFor="amount_from_wallet">
            {translate('amount_from_wallet')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(totalAmount)}
          </div>
        </section>
        <section
          className="flex flex-col mt-6 space-y-2"
          aria-labelledby="amount_to_provider"
        >
          <Label id="amount_to_provider_label" htmlFor="amount_to_provider">
            {translate('amount_to_provider')}{' '}
          </Label>
          <div className="relative mt-2 text-sm text-gray-800">
            {currencyFormatter.format(base_amount)}
          </div>
        </section>
      </div>
    </div>
  )
}

export default PaymentFeesResultsCard
